import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, TextField, InputAdornment, Chip, Paper, Button, Avatar, Skeleton } from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { CustomBreadcrumbs, PageBanner, CustomPagination, CustomTableContainer, CustomTable, CustomTableHead, CustomTableRow, CustomTableCell, CustomTableBody } from '../../common';
import teacherBannerBg from '../../../assets/images/teacher.jpg';
import teacherService from '../../../infrastructure/services/teacherService';

const getProfileImageUrl = (pic) => {
  if (!pic) return null;
  if (pic.startsWith('http://') || pic.startsWith('https://')) {
    return pic;
  }
  return `https://fonixedugrading.blob.core.windows.net/eduone-auth-content/${pic}`;
};

const getInitials = (name) => {
  if (!name) return 'T';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};



export default function TeacherSelection() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const breadcrumbItems = [
    { label: 'Home', href: '/dashboard' },
    { label: 'Exam Management' }
  ];

  const fetchTeachers = async (search, page) => {
    setLoading(true);
    try {
      const data = await teacherService.getTeachers({
        search,
        sectionId: 'exam',
        page,
        pageSize: 10
      });
      if (data && data.isSuccessful) {
        setTeachers(data.listContent || []);
        setTotalPages(data.paginationInfo?.totalPages || 1);
        setTotalItems(data.paginationInfo?.totalItems || 0);
      }
    } catch (err) {
      console.error('Error fetching teachers:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchTeachers(searchQuery, currentPage);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery, currentPage]);

  const safeCurrentPage = Math.min(currentPage, totalPages);

  return (
    <Box sx={{ pb: 5, fontFamily: 'Poppins, sans-serif' }}>
      {/* Breadcrumbs */}
      <CustomBreadcrumbs items={breadcrumbItems} sx={{ mb: 2 }} />

      {/* Page Banner with background teacher.jpg */}
      <PageBanner
        title="Exam Manager"
        description="Select a teacher to view assigned exams, mock tests, and review graded reports."
        backgroundImage={teacherBannerBg}
      />

      <Box sx={{ mt: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: '1.5px solid #e2e8f0',
            borderRadius: '24px',
            bgcolor: '#ffffff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
          }}
        >
          {/* Header and Actions Bar */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'stretch', md: 'center' },
              justifyContent: 'space-between',
              gap: 2,
              mb: 3
            }}
          >
            {/* Title Block */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '20px', color: '#262626', fontFamily: 'Poppins' }}>
                  Choose Your Teacher
                </Typography>
                <Chip
                  label={`${totalItems} Teachers`}
                  size="small"
                  sx={{
                    bgcolor: '#FFF1EE',
                    color: '#FF502D',
                    fontWeight: 500,
                    fontSize: '12px',
                    fontFamily: 'Poppins',
                    borderRadius: '16px',
                    height: '22px'
                  }}
                />
              </Box>
              <Typography
                sx={{
                  color: '#94a3b8',
                  fontSize: '13px',
                  fontFamily: 'Poppins',
                  mt: 0.5
                }}
              >
                Select a teacher to access their course exams and test papers
              </Typography>
            </Box>

            {/* Search Input */}
            <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
              <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
                <TextField
                  placeholder="Search"
                  size="small"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                  sx={{
                    width: { xs: '100%', sm: 260 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      fontFamily: 'Poppins',
                      fontSize: '13px',
                      bgcolor: '#ffffff',
                      '& fieldset': { borderColor: '#cbd5e1' },
                      '&:hover fieldset': { borderColor: '#94a3b8' },
                      '&.Mui-focused fieldset': { borderColor: '#64748b' }
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon sx={{ color: '#94a3b8', fontSize: '18px' }} />
                      </InputAdornment>
                    )
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* Table List of Teachers */}
          <CustomTableContainer sx={{ border: 'none', overflowX: 'auto' }}>
            <CustomTable size="medium">
              <CustomTableHead>
                <CustomTableRow>
                  {[
                    { label: 'Teacher Name' },
                    { label: 'Subjects' },
                    { label: 'Ongoing Exam' },
                    { label: 'Upcoming Exams' },
                    { label: 'Total Exams' }
                  ].map((col) => (
                    <CustomTableCell
                      key={col.label}
                      sx={{
                        color: '#94a3b8',
                        fontWeight: 600,
                        fontSize: '12px',
                        fontFamily: 'Poppins',
                        borderBottom: '1px solid #f1f5f9',
                        py: 2,
                        bgcolor: '#ffffff',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {col.label}
                    </CustomTableCell>
                  ))}
                  {/* Actions column */}
                  <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2, bgcolor: '#ffffff' }} />
                </CustomTableRow>
              </CustomTableHead>
              <CustomTableBody>
                {loading ? (
                  Array.from({ length: 4 }).map((_, idx) => (
                    <CustomTableRow key={idx}>
                      <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Skeleton variant="circular" width={40} height={40} />
                          <Box sx={{ flex: 1 }}>
                            <Skeleton variant="text" width={120} height={18} />
                            <Skeleton variant="text" width={80} height={14} sx={{ mt: 0.5 }} />
                          </Box>
                        </Box>
                      </CustomTableCell>
                      <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Skeleton variant="rectangular" width={60} height={20} sx={{ borderRadius: '8px' }} />
                          <Skeleton variant="rectangular" width={60} height={20} sx={{ borderRadius: '8px' }} />
                        </Box>
                      </CustomTableCell>
                      <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                        <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: '8px' }} />
                      </CustomTableCell>
                      <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                        <Skeleton variant="rectangular" width={90} height={24} sx={{ borderRadius: '8px' }} />
                      </CustomTableCell>
                      <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                        <Skeleton variant="rectangular" width={70} height={24} sx={{ borderRadius: '8px' }} />
                      </CustomTableCell>
                      <CustomTableCell align="right" sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                        <Skeleton variant="rectangular" width={100} height={32} sx={{ borderRadius: '8px' }} />
                      </CustomTableCell>
                    </CustomTableRow>
                  ))
                ) : teachers.length === 0 ? (
                  <CustomTableRow>
                    <CustomTableCell colSpan={6} align="center" sx={{ py: 8, color: '#94a3b8', fontFamily: 'Poppins' }}>
                      No teachers found.
                    </CustomTableCell>
                  </CustomTableRow>
                ) : (
                  teachers.map((teacher) => (
                    <CustomTableRow
                      key={teacher.teacherId}
                      hover
                      onClick={() => navigate(`/exam-manager/exams/${teacher.teacherId}`)}
                      sx={{
                        '&:last-child td': { borderBottom: 'none' },
                        cursor: 'pointer',
                        transition: 'background-color 0.2s',
                        '&:hover': {
                          bgcolor: '#fffbf9 !important',
                          '& td': { borderColor: '#ffece8' }
                        }
                      }}
                    >
                      {/* Teacher Avatar & Name */}
                      <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {/* Avatar */}
                          <Avatar
                            src={getProfileImageUrl(teacher.profilePicture) || undefined}
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              bgcolor: '#f1f5f9',
                              border: '1.5px solid #e2e8f0',
                              color: '#64748b',
                              fontWeight: 600,
                              fontSize: '14px',
                              fontFamily: 'Poppins',
                              flexShrink: 0,
                              '& img': {
                                imageRendering: '-webkit-optimize-contrast'
                              }
                            }}
                          >
                            {getInitials(teacher.teacherName)}
                          </Avatar>

                          <Box>
                            <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#1e293b', fontFamily: 'Poppins' }}>
                              {teacher.teacherName}
                            </Typography>
                            <Typography sx={{ fontSize: '11px', color: '#94a3b8', fontFamily: 'Poppins', mt: 0.25 }}>
                              ID: {teacher.teacherId}
                            </Typography>
                          </Box>
                        </Box>
                      </CustomTableCell>

                      {/* Subjects */}
                      <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: '250px' }}>
                          {teacher.subjects && teacher.subjects.length > 0 ? (
                            teacher.subjects.map((subj, idx) => (
                              <Chip
                                key={idx}
                                label={subj}
                                size="small"
                                sx={{
                                  bgcolor: 'rgba(255, 80, 45, 0.05)',
                                  color: '#ff502d',
                                  fontWeight: 600,
                                  fontFamily: 'Poppins',
                                  borderRadius: '8px',
                                  fontSize: '10px',
                                  height: '20px',
                                  border: '1px solid rgba(255, 80, 45, 0.1)'
                                }}
                              />
                            ))
                          ) : (
                            <Typography sx={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic', fontFamily: 'Poppins' }}>
                              No subjects
                            </Typography>
                          )}
                        </Box>
                      </CustomTableCell>

                      {/* Ongoing Exam */}
                      <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                        <Chip
                          label={teacher.isOngoingExam ? `${teacher.ongoingExamCount || 1} Ongoing` : 'No ongoing'}
                          size="small"
                          sx={{
                            bgcolor: teacher.isOngoingExam ? 'rgba(46, 204, 113, 0.1)' : 'rgba(148, 163, 184, 0.1)',
                            color: teacher.isOngoingExam ? '#2ecc71' : '#64748b',
                            fontWeight: 700,
                            fontFamily: 'Poppins',
                            borderRadius: '8px',
                            fontSize: '11px',
                            height: '24px',
                            px: 1.5,
                            border: teacher.isOngoingExam ? '1px solid rgba(46, 204, 113, 0.2)' : '1px solid rgba(148, 163, 184, 0.2)'
                          }}
                        />
                      </CustomTableCell>

                      {/* Upcoming Exams Count */}
                      <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                        <Typography
                          sx={{
                            fontSize: '12px',
                            fontWeight: 700,
                            color: '#6366f1',
                            fontFamily: 'Poppins',
                            bgcolor: 'rgba(99, 102, 241, 0.05)',
                            display: 'inline-block',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '8px',
                            border: '1px solid rgba(99, 102, 241, 0.1)',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {teacher.upcomingExamCount} Upcoming
                        </Typography>
                      </CustomTableCell>

                      {/* Total Exams Count */}
                      <CustomTableCell sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                        <Typography
                          sx={{
                            fontSize: '12px',
                            fontWeight: 700,
                            color: '#5ca600',
                            fontFamily: 'Poppins',
                            bgcolor: 'rgba(92, 166, 0, 0.05)',
                            display: 'inline-block',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '8px',
                            border: '1px solid rgba(92, 166, 0, 0.1)',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {teacher.totalExamCount} Total
                        </Typography>
                      </CustomTableCell>

                      {/* Action button */}
                      <CustomTableCell align="right" sx={{ borderBottom: '1px solid #f1f5f9', py: 2 }}>
                        <Button
                          variant="contained"
                          size="small"
                          sx={{
                            bgcolor: '#FF502D',
                            color: '#ffffff',
                            fontWeight: 600,
                            fontFamily: 'Poppins',
                            fontSize: '12px',
                            textTransform: 'none',
                            borderRadius: '8px',
                            px: 2.5,
                            py: 0.5,
                            height: '32px',
                            boxShadow: 'none',
                            whiteSpace: 'nowrap',
                            '&:hover': {
                              bgcolor: '#e64525',
                              boxShadow: 'none'
                            }
                          }}
                        >
                          View Exams
                        </Button>
                      </CustomTableCell>
                    </CustomTableRow>
                  ))
                )}
              </CustomTableBody>
            </CustomTable>
          </CustomTableContainer>

          {/* Pagination Section */}
          {totalPages > 1 && (
            <CustomPagination
              currentPage={safeCurrentPage}
              totalPages={totalPages}
              onPageChange={(page) => setCurrentPage(page)}
            />
          )}
        </Paper>
      </Box>
    </Box>
  );
}
